/**
 * The browser's BACK and FORWARD (2026-11-03, operator: "if i click back button, it should stay within opbuttons",
 * pressed with the mouse's back button; then "the other button on the mouse is usually forward, forward needs to work
 * too"). Nothing listened to either, so back left the app. Now one back press steps back ONE thing, the way Esc does
 * on a surface, and at the bare canvas it does nothing: it never leaves OPButtons. Forward undoes a back, as in a
 * browser.
 *
 * The order back takes, top first (`goBack`):
 *   1. a layer a surface registered as an overlay (`onBack(fn)`): the timeline editor's menus, Navigate's account
 *      menu and search;
 *   2. any other menu or dialog on screen, closed the way it closes itself: by an Esc sent to it;
 *   3. the timeline editor, to Navigate's Timelines (where its own logo goes);
 *   4. a section of Navigate: back a view inside Help, else to Navigate's home;
 *   5. Navigate itself, to the canvas;
 *   6. a layer registered for the canvas (`onBack(fn, 'canvas')`): the Inspector's selection, a move, cell editing.
 *
 * FORWARD (`goForward`) reopens what a back closed among the SCREENS (3-5: the editor on the same timeline, the
 * section, the Help page, Navigate), newest first. A menu, a dialog or the Inspector a back closed is not reopened, as
 * a browser never re-opens a dropdown. Opening anything else in between ends it, as a new page does in a browser: each
 * step remembers the screen it left behind, and forward does nothing once the screen is not that one.
 *
 * HOW it holds: three history entries, the app sitting on the middle one. A back press lands on the one under it, a
 * forward press on the one over it; `popstate` steps back or forward one thing and sets the three up again, the app on
 * the middle. They are pushed on the first press or key, never at load: Chrome skips, on back, entries a page pushed
 * without a user's press (its history-manipulation intervention). The mouse's buttons count as that press (measured
 * headless, `_probe_back_mechanics`).
 */

import {
  closeTimelineEditor, openLegacyTimelineEditor, openTimelineEditor, timelineEditorKind, timelineEditorOpen,
  timelineEditorTargetId,
} from '../timeline/store.ts';
import { closeNavigate, navigateOpen, navigatePane, openNavigateAt, type NavPane } from './store.ts';
import { navigateDocked } from './dock.ts';
import { showPane } from './panes.ts';
import { helpBack, helpGo, helpOpen, helpPrevView, helpView } from '../help/store.ts';

type Layer = () => boolean;
const overlays: Layer[] = [];
const canvas: Layer[] = [];

/** Register what a surface closes on back; return true when it closed something. `overlay` layers run before
 *  anything else, newest first; `canvas` ones only when nothing is over the canvas. Returns the unregister. */
export function onBack(fn: Layer, where: 'overlay' | 'canvas' = 'overlay'): () => void {
  const list = where === 'overlay' ? overlays : canvas;
  list.push(fn);
  return () => { const i = list.indexOf(fn); if (i >= 0) list.splice(i, 1); };
}

/** Menus and dialogs on screen that are not one of the big surfaces (Navigate, the timeline editors: they carry
 *  `data-back-surface`) and not inside the timeline editor (it registers its own). Topmost last in the DOM. */
const OVERLAY = '[role="dialog"], [role="alertdialog"], [role="menu"], [data-nav-layer]';
function openOverlay(): HTMLElement | null {
  const all = Array.from(document.querySelectorAll<HTMLElement>(OVERLAY)).filter((el) =>
    !el.hasAttribute('data-back-surface') && !el.closest('.tl2') && el.isConnected && el.getClientRects().length > 0);
  return all[all.length - 1] ?? null;
}

function sendEscape(): void {
  const at = document.activeElement instanceof HTMLElement ? document.activeElement : document.body;
  at.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true, cancelable: true }));
}

/** Which screen is up: the editor and its timeline, Navigate and its section, the Help page. A forward step is only
 *  good while the screen is still the one its back left behind. */
function screenKey(): string {
  return JSON.stringify([
    timelineEditorOpen() ? `${timelineEditorKind()}:${timelineEditorTargetId() ?? ''}` : '',
    navigateOpen() ? navigatePane() : '',
    helpOpen() ? helpView() : null,
  ]);
}

/** The screens a back closed, newest last: how to open each again, and the screen the back left. */
const ahead: Array<{ reopen: () => void; left: string }> = [];
function remember(reopen: () => void): void { ahead.push({ reopen, left: screenKey() }); }

/** Open a section the way a row in Navigate does (Navigate open on it, its own switch on). */
function openSection(p: NavPane): void {
  showPane(p);
  if (!navigateOpen()) openNavigateAt(p);
}

/** Step back one thing. Returns what it did, for the smokes and the log; '' = nothing to step back from. */
export function goBack(): string {
  for (let i = overlays.length - 1; i >= 0; i--) if (overlays[i]!()) return 'overlay';
  const ov = openOverlay();
  if (ov) {
    sendEscape();
    // Closed by its own Esc: done. One that ignores Esc falls through, so back is never stuck on it.
    if (!ov.isConnected || ov.getClientRects().length === 0) return 'menu';
  }
  if (timelineEditorOpen()) {
    const id = timelineEditorTargetId();
    const legacy = timelineEditorKind() === 'legacy';
    closeTimelineEditor();
    openNavigateAt('timelines');
    // Navigate closes as the editor opens, as when a row opens it.
    remember(() => { closeNavigate(); (legacy ? openLegacyTimelineEditor : openTimelineEditor)(id); });
    return 'editor';
  }
  if (navigateOpen()) {
    const pane = navigatePane();
    if (pane === 'help' && helpPrevView()) {
      const view = helpView();
      helpBack();
      remember(() => helpGo(view));
      return 'help';
    }
    if (pane !== 'home') {
      showPane('home');
      // Docked (DEC-268) the home list is the canvas: Navigate stays open only over a section.
      if (navigateDocked()) closeNavigate();
      remember(() => openSection(pane));
      return 'section';
    }
    closeNavigate();
    remember(() => openNavigateAt('home'));
    return 'navigate';
  }
  for (let i = canvas.length - 1; i >= 0; i--) if (canvas[i]!()) return 'canvas';
  return '';
}

/** Undo the last back that closed a screen. '' = nothing ahead, or the screen moved on since (which ends it). */
export function goForward(): string {
  const next = ahead.pop();
  if (!next) return '';
  if (next.left !== screenKey()) { ahead.length = 0; return ''; }
  next.reopen();
  return 'forward';
}

type BackWindow = Window & {
  __opbBack?: { last: string; count: number; forwards: number };
  __opbBackGo?: () => string;
  __opbForwardGo?: () => string;
};
/** The three entries' marks in `history.state`: under the app (a back lands here), the app, over it (a forward). */
const KEY = 'opbuttons-nav';
type Mark = 'under' | 'app' | 'over';
const markOf = (st: unknown): Mark | null => {
  const m = (st as { [KEY]?: Mark } | null)?.[KEY];
  return m === 'under' || m === 'app' || m === 'over' ? m : null;
};
/** The mouse's back and forward buttons (MouseEvent.button 3 and 4). */
const sideButton = (e: Event) => (e as MouseEvent).button === 3 || (e as MouseEvent).button === 4;

/** Install once (App). Survives Vite's hot reload: the listeners stay, and call the newest `goBack` / `goForward`. */
export function installBackGuard(): void {
  const w = window as BackWindow;
  w.__opbBackGo = goBack;
  w.__opbForwardGo = goForward;
  if (w.__opbBack) return;
  w.__opbBack = { last: '', count: 0, forwards: 0 };
  /** The three entries, the app on the middle: under (this entry, marked), app, over; then one step back onto app.
   *  The step back is the browser's own and lands as a popstate on `app`, which does nothing. */
  // Our own steps back onto the app, still to land. Counted, not only read off the entry's mark: a page that
  // replaces the state (ConnectPage's pairing link does, with null) would wipe it.
  let ownSteps = 0;
  const stepBackOntoApp = () => { ownSteps++; history.back(); };
  const setUp = () => {
    history.replaceState({ ...(history.state ?? {}), [KEY]: 'under' }, '');
    history.pushState({ [KEY]: 'app' }, '');
    history.pushState({ [KEY]: 'over' }, '');
    stepBackOntoApp();
  };
  // A reload sits on the app entry already: the three are still there.
  let armed = markOf(history.state) === 'app';
  const arm = (e: Event) => {
    if (armed || !e.isTrusted) return;
    armed = true;
    setUp();
  };
  // The mouse's back button is a PRESS as well as a back: on its own, the press reached every "a press outside closes
  // me" handler, so one back closed a menu (the press) and then the section under it (the back), two things at once
  // (back_button_stays_in_app step 3, 2026-11-03). Its press, release and clicks stop here, before the app's handlers
  // (these are the first capture listeners on window: App installs them as it starts). The browser still goes back or
  // forward: that is the release's default action, and nothing here prevents it.
  const swallow = (e: Event) => { if (sideButton(e)) e.stopImmediatePropagation(); };
  window.addEventListener('pointerdown', (e) => { arm(e); swallow(e); }, true);
  window.addEventListener('keydown', arm, true);
  for (const type of ['mousedown', 'pointerup', 'mouseup', 'click', 'auxclick'] as const) window.addEventListener(type, swallow, true);
  window.addEventListener('popstate', (e) => {
    const mark = markOf(e.state);
    // Our own step back onto the app (its mark, or a count still owed when a page wiped the mark): nothing to do.
    if (mark === 'app' || (mark === null && ownSteps > 0)) { if (ownSteps > 0) ownSteps--; return; }
    if (mark === 'over') {
      const did = w.__opbForwardGo?.() ?? '';
      w.__opbBack = { ...w.__opbBack!, last: did, forwards: w.__opbBack!.forwards + 1 };
      stepBackOntoApp(); // onto the app again; `over` stays ahead for the next forward
      return;
    }
    // `under`, or an entry from before OPButtons armed (a page the tab showed earlier never reaches here: it is
    // another document). Step back, then set the three up again ON this entry, synchronously, so a second quick back
    // lands on our entries and never leaves the app.
    const did = w.__opbBackGo?.() ?? '';
    w.__opbBack = { ...w.__opbBack!, last: did, count: w.__opbBack!.count + 1 };
    setUp();
  });
}
